// src/components/collaboration/CollaborationTimeline.jsx
import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  MessageSquare, Users, Send, Building2, FolderKanban,
  Loader2, AlertCircle, X, GitBranch, ArrowLeft,
  Image as ImageIcon, FileText, Paperclip, Trash2,
  CheckCircle2, AlertTriangle, RotateCcw, Pencil, Circle,
  PackageCheck, Share2, CalendarClock, ShieldOff,
} from "lucide-react";
import { useCollabRailStore } from "../../stores/useCollabRailStore";
import QuickActions from "./QuickActions";
import { useCollaborationStore } from "../../stores/useCollaborationStore";
import { useProjectStore } from "../../stores/useProjectStore";
import { useAuthStore } from "../../stores/useAuthStore";
import { searchUsers } from "../../api/collabApi";

// The NARRATIVE set — this renders inside a sentence ("handed off to the
// owner"), which is why it is not CASE_ROLE_LABEL. Both live in roleLabels.js;
// splitting formal from narrative across two files is how five declarations
// became five vocabularies.
import { CASE_ROLE_MEANING } from "../../../../admin-api/lib/padrekko-qc/roleLabels.js";

const EMPTY_ARRAY = [];

function cx(...classes) {
  return classes.filter(Boolean).join(" ");
}

/**
 * Who wrote an event or a reply, as the rail prints it (REM-426).
 *
 * author_display, or "Unknown". There is no author_username fallback any more:
 * that field was the author's email address, sent to every org on a shared
 * thread, and the server no longer returns it. "Unknown" is now the only
 * backstop, so every author site goes through here rather than restating the
 * chain; the link row used to have no backstop at all and could print nothing.
 */
export function authorName(item) {
  return item?.author_display || "Unknown";
}

function timeLabel(ts) {
  const d = new Date(ts);
  return d.toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

function parseContextLabel(contextKey, projectName, orgName, caseName) {
  if (typeof contextKey !== "string") return { type: "unknown", label: "unknown" };
  if (contextKey.startsWith("case:"))    return { type: "Case",    label: caseName    || contextKey.replace("case:", "") };
  if (contextKey.startsWith("org:"))     return { type: "Org",     label: orgName     || contextKey.replace("org:", "") };
  if (contextKey.startsWith("product:")) return { type: "Product", label: projectName || contextKey.replace("product:", "") };
  if (contextKey.startsWith("task:"))    return { type: "Task",    label: projectName || contextKey.replace("task:", "") };
  if (contextKey === "global")           return { type: "Project", label: projectName || "No project" };
  return { type: "View", label: projectName || contextKey };
}

// ─── Scope toggle ─────────────────────────────────────────────────────────────

function ScopeToggle({
  scope, onChange, projectUnread, orgUnread, caseUnread,
  caseAvailable, casePillVisible, caseCount, onOpenPicker,
}) {
  // Case button visibility:
  //   - casePillVisible (user has any cases) → button rendered
  //   - caseAvailable (caseInfo loaded) → click switches scope to "case"
  //   - !caseAvailable but casePillVisible → click navigates to PadRekko
  //     picker (onOpenPicker), letting the user pick which case to open

  // Pulse keyed on caseAvailable (case opens), NOT on casePillVisible
  // (which changes when the user gains/loses any case). One-shot when
  // a case is first opened in the session.
  const [casePulse, setCasePulse] = useState(false);
  const prevAvailable = useRef(caseAvailable);
  useEffect(() => {
    const transitionedToAvailable = !prevAvailable.current && caseAvailable;
    prevAvailable.current = caseAvailable;
    if (transitionedToAvailable) {
      setCasePulse(true);
      const t = setTimeout(() => setCasePulse(false), 1500);
      return () => clearTimeout(t);
    }
  }, [caseAvailable]);

  const caseLabel = caseCount > 1 ? `Case (${caseCount})` : "Case";

  const items = [
    { id: "org",     Icon: Building2,    label: "Org",     unread: orgUnread },
    { id: "project", Icon: FolderKanban, label: "Project", unread: projectUnread },
  ];
  if (casePillVisible) {
    items.push({ id: "case", Icon: GitBranch, label: caseLabel, unread: caseUnread });
  }
  return (
    <div className="flex items-center gap-2 px-5 pb-3">
      {items.map(({ id, Icon, label, unread }) => (
        <button
          key={id}
          onClick={() => {
            if (id === "case" && !caseAvailable) {
              onOpenPicker?.();
            } else {
              onChange(id);
            }
          }}
          title={
            id === "case" && !caseAvailable
              ? "Open the PadRekko picker to view your cases"
              : undefined
          }
          className={cx(
            "flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-medium transition-colors duration-100",
            "ring-1 ring-inset",
            scope === id
              ? "bg-white/10 text-zinc-100 ring-white/20"
              : id === "case" && casePulse
                ? "bg-teal-500/15 text-teal-200 ring-teal-400/50 animate-[pulse_1.5s_ease-in-out_1]"
                : "bg-transparent text-zinc-500 ring-white/10 hover:text-zinc-300 hover:bg-white/5"
          )}
        >
          <Icon className="w-3.5 h-3.5" />
          {label}
          {unread && scope !== id && (
            <span className="w-1.5 h-1.5 rounded-full bg-sky-400 inline-block" />
          )}
        </button>
      ))}
    </div>
  );
}

// ─── Member avatars ───────────────────────────────────────────────────────────

const AVATAR_COLORS = [
  "bg-blue-500",
  "bg-emerald-500",
  "bg-amber-500",
  "bg-rose-500",
  "bg-violet-500",
];

function MemberAvatar({ initials, colorIndex = 0 }) {
  return (
    <div className={cx(
      "w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold",
      "ring-2 ring-zinc-900 -mr-1",
      AVATAR_COLORS[colorIndex % AVATAR_COLORS.length],
      "text-white"
    )}>
      {initials}
    </div>
  );
}

// ─── Tabs ─────────────────────────────────────────────────────────────────────

const TABS = [
  { id: "thread",   label: "Thread" },
  { id: "activity", label: "Activity" },
  { id: "links",    label: "Links" },
];

function TabRow({ active, onChange, unreadCount }) {
  return (
    <div className="flex gap-2 px-5 pb-4">
      {TABS.map(({ id, label }) => (
        <button
          key={id}
          onClick={() => onChange(id)}
          className={cx(
            "flex items-center gap-1.5 px-4 py-2 rounded-xl text-[10px] font-medium transition-colors duration-100",
            "ring-1 ring-inset",
            active === id
              ? "bg-white/10 text-zinc-100 ring-white/20"
              : "bg-transparent text-zinc-500 ring-white/10 hover:text-zinc-300 hover:bg-white/5"
          )}
        >
          {label}
          {id === "thread" && unreadCount > 0 && (
            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold bg-sky-500/20 text-sky-300 ring-1 ring-sky-500/30">
              {unreadCount}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

// ─── Mention autocomplete ─────────────────────────────────────────────────────

function MentionDropdown({ query, onSelect }) {
  const [users, setUsers]   = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // query === null means @ not active; query === "" means @ typed, no filter yet
    if (query === null) { setUsers([]); return; }
    setLoading(true);
    searchUsers(query)
      .then(({ users }) => setUsers(users || []))
      .catch(() => setUsers([]))
      .finally(() => setLoading(false));
  }, [query]);

  if (query === null) return null;

  return (
    <div className="absolute top-full left-0 right-0 mt-1 rounded-xl bg-zinc-900 ring-1 ring-white/15 overflow-y-auto z-20 shadow-xl max-h-48">
      {loading && (
        <div className="flex items-center gap-2 px-3 py-2 text-[10px] text-zinc-500">
          <Loader2 className="w-3 h-3 animate-spin" /> Searching…
        </div>
      )}
      {!loading && users.length === 0 && (
        <div className="px-3 py-2 text-[10px] text-zinc-500">No users found</div>
      )}
      {users.map((u) => (
        <button
          key={u.id}
          onMouseDown={(e) => { e.preventDefault(); onSelect(u); }}
          className="w-full flex items-center gap-2 px-3 py-2 hover:bg-white/10 transition-colors text-left"
        >
          <div className="w-6 h-6 rounded-full bg-zinc-700 flex items-center justify-center text-[10px] font-bold text-zinc-300 shrink-0">
            {(u.display_name || u.username)[0].toUpperCase()}
          </div>
          <div>
            <div className="text-[10px] text-zinc-200">{u.display_name || u.username}</div>
            <div className="text-[9px] text-zinc-500">@{u.username.includes('@') ? u.username.split('@')[0] : u.username}</div>
          </div>
        </button>
      ))}
    </div>
  );
}

// ─── Composer ────────────────────────────────────────────────────────────────

function Composer({ contextKey, scope, onSubmit, authorDisplay, seed }) {
  const [draft, setDraft]       = useState("");
  const [expanded, setExpanded] = useState(false);
  const [sending, setSending]   = useState(false);
  const [mentionQuery, setMentionQuery] = useState(null);
  const textareaRef = useRef(null);

  // Open and pre-fill when a quick action pushes a seed
  useEffect(() => {
    if (!seed?.text) return;
    setDraft(seed.text);
    setExpanded(true);
    setTimeout(() => {
      const el = textareaRef.current;
      if (el) { el.focus(); el.setSelectionRange(el.value.length, el.value.length); }
    }, 0);
  }, [seed]);

  const placeholder = scope === "org"
    ? "Post to the org thread…"
    : "Add a note to this context…";

  const handleChange = (e) => {
    const val = e.target.value;
    setDraft(val);
    const cursor = e.target.selectionStart;
    const textUpToCursor = val.slice(0, cursor);
    // mentionMatch[1] is "" when just @ is typed — that's valid, shows all members
    const mentionMatch = textUpToCursor.match(/@([\w.-]*)$/);
    setMentionQuery(mentionMatch ? mentionMatch[1] : null);
  };

  const handleMentionSelect = (user) => {
    const cursor = textareaRef.current?.selectionStart || draft.length;
    const handle = user.username.includes('@') ? user.username.split('@')[0] : user.username;
    const before = draft.slice(0, cursor).replace(/@[\w.-]*$/, `@${handle} `);
    const after  = draft.slice(cursor);
    setDraft(before + after);
    setMentionQuery(null);
    textareaRef.current?.focus();
  };

  const handleSubmit = async () => {
    const text = draft.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      await onSubmit(text);
      setDraft("");
      setExpanded(false);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); handleSubmit(); }
    if (e.key === "Escape") { setExpanded(false); setDraft(""); setMentionQuery(null); }
  };

  const initials = authorDisplay
    ? authorDisplay.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase()
    : "?";

  return (
    <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 relative">
      {/* Always-visible avatar row */}
      <div
        className="flex items-center gap-3 px-3 py-3 cursor-text"
        onClick={() => setExpanded(true)}
      >
        <div className="w-7 h-7 rounded-full bg-teal-600 flex items-center justify-center text-[10px] font-bold text-white shrink-0">
          {initials}
        </div>
        {!expanded && (
          <span className="text-[10px] text-zinc-500">Add a note to this context</span>
        )}
      </div>

      {expanded && (
        <>
          <div className="px-3 pb-2">
            <textarea
              ref={textareaRef}
              autoFocus
              rows={3}
              value={draft}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              className="w-full resize-none bg-zinc-950/40 rounded-xl ring-1 ring-white/10 px-3 py-2 text-[10px] text-zinc-200 placeholder:text-zinc-600 focus:outline-none leading-relaxed"
            />
            <MentionDropdown query={mentionQuery} onSelect={handleMentionSelect} />
          </div>
          <div className="flex items-center justify-between px-3 py-2 border-t border-white/10">
            <span className="text-[9px] text-zinc-600">⌘↵ to send · @mention</span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => { setExpanded(false); setDraft(""); }}
                className="px-3 py-1.5 text-[10px] text-zinc-500 hover:text-zinc-300 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={!draft.trim() || sending}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-sky-500/15 text-sky-200 text-[10px] font-medium ring-1 ring-inset ring-sky-500/25 transition hover:bg-sky-500/20 disabled:opacity-40"
              >
                {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                {sending ? "Sending…" : "Send"}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Inline reply composer ───────────────────────────────────────────────────

function ReplyComposer({ onSubmit, onCancel, authorDisplay }) {
  const [draft, setDraft]     = useState("");
  const [sending, setSending] = useState(false);

  const initials = authorDisplay
    ? authorDisplay.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase()
    : "?";

  const handleSubmit = async () => {
    const text = draft.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      await onSubmit(text);
      setDraft("");
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); handleSubmit(); }
    if (e.key === "Escape") onCancel();
  };

  return (
    <div className="flex gap-2 mt-2">
      <div className="w-5 h-5 rounded-full bg-teal-600 flex items-center justify-center text-[9px] font-bold text-white shrink-0 mt-1">
        {initials}
      </div>
      <div className="flex-1 rounded-xl bg-zinc-900 ring-1 ring-white/10 overflow-hidden">
        <textarea
          autoFocus
          rows={2}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Write a reply… (⌘↵ to send)"
          className="w-full resize-none bg-transparent px-3 py-2 text-[10px] text-zinc-200 placeholder:text-zinc-600 focus:outline-none leading-relaxed"
        />
        <div className="flex items-center justify-end gap-1.5 px-2 py-1.5 border-t border-white/10">
          <button
            onClick={onCancel}
            className="px-2.5 py-1 text-[10px] text-zinc-500 hover:text-zinc-300 transition"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!draft.trim() || sending}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-sky-500/15 text-sky-200 text-[10px] font-medium ring-1 ring-inset ring-sky-500/25 transition hover:bg-sky-500/20 disabled:opacity-40"
          >
            {sending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
            {sending ? "…" : "Reply"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Timeline item — dot-track style matching the mockup ─────────────────────

const EVENT_DOT = {
  comment: "bg-sky-400",
  link:    "bg-emerald-400",
  system:  "bg-zinc-600",
  status:  "bg-violet-400",
  flag:    "bg-red-400",
};

const EVENT_TAG = {
  comment: "text-zinc-400 ring-zinc-700",
  link:    "text-emerald-400 ring-emerald-900",
  system:  "text-zinc-500 ring-zinc-800",
  status:  "text-violet-400 ring-violet-900",
  flag:    "text-red-400 ring-red-900",
};

// D2.4 — case event renderers. event_type='system' with
// metadata.event_subtype discriminating between case_stage_transition
// and case_created. Both render in any rail the user has open (case
// rail when viewing the case, org rail for the workflow signal).
function CaseStageTransitionEvent({ item }) {
  const m = item.metadata || {};
  const pad = m.pad_summary || {};
  const hasCounts = pad.completed != null || pad.skipped != null;
  return (
    <div className="text-[10px] leading-snug">
      <div className="flex items-center gap-1.5 text-zinc-300">
        <GitBranch size={11} className="text-teal-400 shrink-0" />
        <span className="font-semibold text-zinc-100 truncate">{m.case_name || "Case"}</span>
        <span className="text-zinc-500">·</span>
        <span className="text-zinc-400 text-[9px] font-mono uppercase tracking-widest whitespace-nowrap">
          {m.from_stage} → {m.to_stage}
        </span>
      </div>
      <div className="text-[10px] text-zinc-500 mt-0.5">
        by {authorName(item)}
        {hasCounts && (
          <> · {pad.completed ?? 0} completed, {pad.skipped ?? 0} skipped</>
        )}
      </div>
      {m.warning && (
        <div className="text-[9px] text-amber-400 italic mt-0.5">⚠ {m.warning}</div>
      )}
    </div>
  );
}

function CaseCreatedEvent({ item }) {
  const m = item.metadata || {};
  const partCount = Array.isArray(m.participants) ? m.participants.length : 0;
  return (
    <div className="text-[10px] leading-snug">
      <div className="flex items-center gap-1.5 text-zinc-300">
        <GitBranch size={11} className="text-emerald-400 shrink-0" />
        <span className="font-semibold text-zinc-100 truncate">{m.case_name || "Case"}</span>
        <span className="text-zinc-500">·</span>
        <span className="text-zinc-400 text-[9px] font-mono uppercase tracking-widest">created</span>
      </div>
      <div className="text-[10px] text-zinc-500 mt-0.5">
        by {authorName(item)}
        {partCount > 0 && <> · {partCount} participants</>}
      </div>
    </div>
  );
}

// Day 4 — autodetect failure event renderer. pad_detection_completed
// events are filtered out server-side (rail-visibility split); only
// failures surface here. Muted-red tone signals retry-worthy without
// shouting like a flag event.
function PadDetectionFailedEvent({ item }) {
  const m = item.metadata || {};
  return (
    <div className="text-[10px] leading-snug">
      <div className="flex items-center gap-1.5 text-zinc-300">
        <AlertCircle size={11} className="text-red-400 shrink-0" />
        <span className="font-semibold text-zinc-100 truncate">
          Autodetect failed on {m.pad_name || "(unknown pad)"}
        </span>
      </div>
      {m.error && (
        <div className="text-[10px] text-red-300/80 mt-0.5 leading-snug break-words">
          {m.error}
        </div>
      )}
      <div className="text-[10px] text-zinc-500 mt-0.5">
        by {authorName(item)}
      </div>
    </div>
  );
}

// Phase A — reverse stage transition renderer. Mirrors
// CaseStageTransitionEvent but with ArrowLeft icon + amber tone, and
// includes the reason text + disputed finding count.
const STAGE_REVERSE_LABELS = {
  dh_review:       "DH Review",
  customer_review: "Customer Review",
  operator_review: "Operator Review",
};
function StageReverseTransitionEvent({ item }) {
  const m = item.metadata || {};
  const toLabel = STAGE_REVERSE_LABELS[m.to_stage] ?? m.to_stage;
  const disputed = typeof m.disputed_finding_count === "number"
    ? m.disputed_finding_count
    : 0;
  return (
    <div className="text-[10px] leading-snug">
      <div className="flex items-center gap-1.5 text-zinc-300">
        <ArrowLeft size={11} className="text-amber-400 shrink-0" />
        <span className="font-semibold text-zinc-100 truncate">{m.case_name || "Case"}</span>
        <span className="text-zinc-500">·</span>
        <span className="text-amber-300/90 text-[9px] font-mono uppercase tracking-widest whitespace-nowrap">
          {m.from_stage === "complete" ? "reopened to" : "sent back to"} {toLabel}
        </span>
      </div>
      <div className="text-[10px] text-zinc-500 mt-0.5">
        by {authorName(item)}
        {disputed > 0 && (
          <> · {disputed} finding{disputed === 1 ? "" : "s"} flagged</>
        )}
      </div>
      {m.reason && (
        <div className="text-[10px] text-amber-300/80 italic mt-0.5 leading-snug
                        line-clamp-3 break-words">
          "{m.reason}"
        </div>
      )}
    </div>
  );
}

// Day 8 Phase A — case-level note renderer. Subtle slate tone (notes are
// commentary, not workflow events). Truncated body preview from metadata;
// "…" appended when body_truncated is true. Click does nothing for now
// (Phase B+ could deep-link to the case notes panel).
function CaseNoteAddedEvent({ item }) {
  const m = item.metadata || {};
  const preview = m.body_preview || "";
  return (
    <div className="text-[10px] leading-snug">
      <div className="flex items-center gap-1.5 text-zinc-300">
        <MessageSquare size={11} className="text-slate-300 shrink-0" />
        <span className="font-semibold text-zinc-100 truncate">{m.case_name || "Case"}</span>
        <span className="text-zinc-500">·</span>
        <span className="text-slate-400 text-[9px] font-mono uppercase tracking-widest whitespace-nowrap">
          note added
        </span>
      </div>
      <div className="text-[10px] text-zinc-500 mt-0.5">
        by {authorName(item)}
      </div>
      {preview && (
        <div className="text-[10px] text-slate-300 mt-0.5 leading-snug
                        line-clamp-3 break-words">
          {preview}{m.body_truncated ? "…" : ""}
        </div>
      )}
    </div>
  );
}

// Attachment added/removed. Payload carries attachment_type (photo|document),
// filename, and finding_id (null = pad-level). We can't resolve a human
// finding/pad label from the id here, so we convey scope ("a finding" / "the
// pad") + the filename, matching the note-added event's shape.
// 099's handoff events: release, share, and a change of response deadline.
// One renderer for three subtypes because they are one family — a handoff fact
// with an actor, two of which carry the deadline that fact set.
//
// ── WHY NOT THE GENERIC LINE ─────────────────────────────────────────────────
//
// GENERIC_IS_FINE exists for events whose whole content is "this happened".
// These are not those. `case_shared` and `response_deadline_set` carry the date
// the respondent is now held to, and the generic line drops metadata it does
// not understand — by design. Dropping the deadline from the event that sets it
// would leave a rail row that names an act and omits its only consequence.
//
// ── THE ROLE, NOT THE ORG ────────────────────────────────────────────────────
//
// `by_role` is rendered through the same rule the subtype names follow: the
// label describes the ROLE that acted, never the organisation filling it. That
// is stageLabels.js's rule ("Pending UL review" shipped one customer's initials
// to every tenant) applied to the rail.
const HANDOFF_EVENTS = {
  case_released:        { Icon: PackageCheck,  label: "released",       tone: "text-sky-300" },
  case_shared:          { Icon: Share2,        label: "shared",         tone: "text-emerald-300" },
  response_deadline_set: { Icon: CalendarClock, label: "deadline set",  tone: "text-amber-300" },
  // REM-576: the owner withdrew the share. The reason is never in the payload
  // (it is in the case history); a move back to customer_review is.
  case_share_revoked:   { Icon: ShieldOff,     label: "share revoked",  tone: "text-amber-300" },
};

/**
 * `days_30` → `30-day`. A DISPLAY transform, not a second declaration of the
 * mapping: the authoritative date is already in the payload as
 * response_deadline_date, and this only labels which option produced it. An
 * unrecognised basis renders as-is rather than being guessed at.
 */
function deadlineBasisLabel(basis) {
  const m = /^days_(\d+)$/.exec(String(basis || ""));
  return m ? `${m[1]}-day` : (basis || null);
}

function CaseHandoffEvent({ item, subtype }) {
  const m = item.metadata || {};
  const spec = HANDOFF_EVENTS[subtype];
  const { Icon, label, tone } = spec;
  const role = CASE_ROLE_MEANING[m.by_role] || null;
  const basis = deadlineBasisLabel(m.response_deadline_basis);
  const due = m.response_deadline_date || null;
  return (
    <div className="text-[10px] leading-snug">
      <div className="flex items-center gap-1.5 text-zinc-300">
        <Icon size={11} className={`${tone} shrink-0`} />
        <span className="font-semibold text-zinc-100 truncate">{m.case_name || "Case"}</span>
        <span className="text-zinc-500">·</span>
        <span className="text-slate-400 text-[9px] font-mono uppercase tracking-widest whitespace-nowrap">
          {label}
        </span>
      </div>
      <div className="text-[10px] text-zinc-500 mt-0.5">
        by {authorName(item)}
        {role && <> · {role}</>}
      </div>
      {due && (
        <div className={`text-[10px] ${tone} mt-0.5 leading-snug`}>
          response due {due}{basis ? ` (${basis})` : ""}
        </div>
      )}
      {m.moved_back === true && (
        <div className={`text-[10px] ${tone} mt-0.5 leading-snug`}>
          moved back to {STAGE_REVERSE_LABELS[m.to_stage] ?? m.to_stage}
        </div>
      )}
    </div>
  );
}

/**
 * REM-576: the one row an operator's org gets when the owner revokes its
 * share, written to an org that can no longer see the case (the deliberate D5
 * exception). Its payload is the case's name and the fact, and this renders
 * exactly that: no actor, no role, no reason, nothing to open.
 */
function CaseAccessWithdrawnEvent({ item }) {
  const m = item.metadata || {};
  return (
    <div className="text-[10px] leading-snug">
      <div className="flex items-center gap-1.5 text-zinc-300">
        <ShieldOff size={11} className="text-amber-300 shrink-0" />
        <span className="font-semibold text-zinc-100 truncate">{m.case_name || "Case"}</span>
        <span className="text-zinc-500">·</span>
        <span className="text-slate-400 text-[9px] font-mono uppercase tracking-widest whitespace-nowrap">
          access withdrawn
        </span>
      </div>
      <div className="text-[10px] text-zinc-500 mt-0.5">
        Your organization can no longer open this case.
      </div>
    </div>
  );
}

/**
 * Subtype → its structured renderer.
 *
 * A TABLE, not fourteen inline comparisons. The comparisons were how three
 * subtypes came to have no renderer without anyone noticing: nothing lists
 * what is handled, so nothing can be compared against what is emitted.
 */
export const CASE_EVENT_RENDERERS = Object.freeze({
  case_stage_transition: (item) => <CaseStageTransitionEvent item={item} />,
  case_created: (item) => <CaseCreatedEvent item={item} />,
  pad_detection_failed: (item) => <PadDetectionFailedEvent item={item} />,
  stage_reverse_transition: (item) => <StageReverseTransitionEvent item={item} />,
  case_note_added: (item) => <CaseNoteAddedEvent item={item} />,
  attachment_added: (item) => <AttachmentEvent item={item} />,
  attachment_deleted: (item) => <AttachmentEvent item={item} removed />,
  finding_completed: (item) => <CompletionEvent item={item} mode="completed" />,
  finding_not_completed: (item) => <CompletionEvent item={item} mode="not_completed" />,
  finding_completion_reset: (item) => <CompletionEvent item={item} mode="reset" />,
  kicked_back_to_dh: (item) => <KickbackEvent item={item} subtype="kicked_back_to_dh" />,
  dh_resubmitted_after_kickback: (item) => (
    <KickbackEvent item={item} subtype="dh_resubmitted_after_kickback" />
  ),
  finding_modified_by_customer: (item) => <KickbackEvent item={item} subtype="finding_modified_by_customer" />,
  finding_deleted_by_customer: (item) => <KickbackEvent item={item} subtype="finding_deleted_by_customer" />,
  case_released: (item) => <CaseHandoffEvent item={item} subtype="case_released" />,
  case_shared: (item) => <CaseHandoffEvent item={item} subtype="case_shared" />,
  response_deadline_set: (item) => <CaseHandoffEvent item={item} subtype="response_deadline_set" />,
  case_share_revoked: (item) => <CaseHandoffEvent item={item} subtype="case_share_revoked" />,
  case_access_withdrawn: (item) => <CaseAccessWithdrawnEvent item={item} />,
});

/** `map_annotation_added` → `Map annotation added`. */
export function humaniseSubtype(subtype) {
  if (!subtype) return "Case event";
  return String(subtype).replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());
}

/**
 * An event this file has never heard of.
 *
 * ── WHY A LINE AND NOT NOTHING ───────────────────────────────────────────────
 *
 * The old fallback was gated on `!item.metadata?.event_subtype`, so it fired
 * only when there was NO subtype — meaning a subtype with no renderer got no
 * content at all, while the row chrome around it (timestamp, tag, reply
 * button) rendered anyway. Empty rows on the rail, one per real event.
 *
 * The same failure as buildProvenanceTrail folding validations away: a list of
 * known cases with no honest default, where the unknown case produces silence
 * that looks like a rendered thing.
 *
 * Rendering nothing at all would be worse here, not better. These are real
 * events someone caused, the header counts what the query returned, and a
 * hidden row makes the count disagree with the list — the same silent
 * absorption in a new place. So: say what happened, say who did it, and do not
 * guess at metadata this file does not understand.
 */
function UnhandledCaseEvent({ item }) {
  const m = item.metadata || {};
  return (
    <div className="text-[10px] leading-snug">
      <div className="flex items-center gap-1.5 text-zinc-300">
        <Circle size={9} className="text-slate-500 shrink-0" />
        <span className="font-semibold text-zinc-100 truncate">{m.case_name || "Case"}</span>
        <span className="text-zinc-500">·</span>
        <span className="text-slate-400 text-[9px] font-mono uppercase tracking-widest whitespace-nowrap">
          {humaniseSubtype(m.event_subtype)}
        </span>
      </div>
      <div className="text-[10px] text-zinc-500 mt-0.5">
        by {authorName(item)}
      </div>
    </div>
  );
}

/** Dispatch, with the generic line as the default rather than the absence of one. */
export function renderCaseEvent(item) {
  const render = CASE_EVENT_RENDERERS[item.metadata?.event_subtype];
  return render ? render(item) : <UnhandledCaseEvent item={item} />;
}

function AttachmentEvent({ item, removed = false }) {
  const m = item.metadata || {};
  const kind = m.attachment_type === "document" ? "document" : "photo";
  const scope = m.finding_id ? "a finding" : "the pad";
  const Icon = removed ? Trash2 : (kind === "document" ? FileText : ImageIcon);
  const verb = removed ? "removed" : "added";
  return (
    <div className="text-[10px] leading-snug">
      <div className="flex items-center gap-1.5 text-zinc-300">
        <Icon size={11} className="text-slate-300 shrink-0" />
        <span className="font-semibold text-zinc-100 truncate">{m.case_name || "Case"}</span>
        <span className="text-zinc-500">·</span>
        <span className="text-slate-400 text-[9px] font-mono uppercase tracking-widest whitespace-nowrap">
          {kind} {verb}
        </span>
      </div>
      <div className="text-[10px] text-zinc-500 mt-0.5">
        {removed ? "removed" : `added to ${scope}`} by{" "}
        {authorName(item)}
      </div>
      {m.filename && (
        <div className="flex items-center gap-1 text-[10px] text-slate-300 mt-0.5 min-w-0">
          <Paperclip size={9} className="text-slate-500 shrink-0" />
          <span className="truncate">{m.filename}</span>
        </div>
      )}
    </div>
  );
}

// Phase 2 — per-finding kickback-loop events. One renderer, four modes:
//   kicked_back_to_dh             customer → DH ("sent to DreamHammer") + note
//   dh_resubmitted_after_kickback DH → customer ("reworked & returned")
//   finding_modified_by_customer        customer edited a finding ("finding updated")
//   finding_deleted_by_customer         customer removed a finding ("removed") + reason
const KICKBACK_RENDER = {
  kicked_back_to_dh:             { Icon: ArrowLeft, tone: "text-amber-400",  labelTone: "text-amber-300/90",  label: "sent to DreamHammer",  noteKey: "note",   noteTone: "text-amber-300/80" },
  dh_resubmitted_after_kickback: { Icon: RotateCcw, tone: "text-teal-400",   labelTone: "text-teal-300/90",   label: "reworked & returned",  noteKey: null,     noteTone: null },
  finding_modified_by_customer:        { Icon: Pencil,    tone: "text-slate-300",  labelTone: "text-slate-400",     label: "finding updated",      noteKey: null,     noteTone: null },
  finding_deleted_by_customer:         { Icon: Trash2,    tone: "text-rose-300",   labelTone: "text-rose-300/90",   label: "finding removed",      noteKey: "reason", noteTone: "text-rose-300/80" },
};
function KickbackEvent({ item, subtype }) {
  const m = item.metadata || {};
  const cfg = KICKBACK_RENDER[subtype];
  if (!cfg) return null;
  const Icon = cfg.Icon;
  const noteText = cfg.noteKey ? m[cfg.noteKey] : null;
  const findingLabel = typeof m.idx === "number" ? `Finding ${m.idx + 1}` : "A finding";
  return (
    <div className="text-[10px] leading-snug">
      <div className="flex items-center gap-1.5 text-zinc-300">
        <Icon size={11} className={cx(cfg.tone, "shrink-0")} />
        <span className="font-semibold text-zinc-100 truncate">{m.case_name || "Case"}</span>
        <span className="text-zinc-500">·</span>
        <span className={cx(cfg.labelTone, "text-[9px] font-mono uppercase tracking-widest whitespace-nowrap")}>
          {cfg.label}
        </span>
      </div>
      <div className="text-[10px] text-zinc-500 mt-0.5">
        {findingLabel} · by {authorName(item)}
      </div>
      {noteText && (
        <div className={cx("text-[10px] italic mt-0.5 leading-snug line-clamp-3 break-words", cfg.noteTone)}>
          "{noteText}"
        </div>
      )}
    </div>
  );
}

// Operator remediation completion status change. Payload carries the new
// status + finding ref; never the note contents (consistent with note/email
// events). `mode` selects the icon/verb.
function CompletionEvent({ item, mode }) {
  const m = item.metadata || {};
  const findingRef = Number.isInteger(m.finding_idx) ? `Finding ${m.finding_idx + 1}` : "a finding";
  const cfg = {
    completed: { Icon: CheckCircle2, color: "text-emerald-400", verb: "marked completed" },
    not_completed: { Icon: AlertTriangle, color: "text-amber-400", verb: "marked not completed" },
    reset: { Icon: RotateCcw, color: "text-slate-400", verb: "reset to pending" },
  }[mode];
  return (
    <div className="text-[10px] leading-snug">
      <div className="flex items-center gap-1.5 text-zinc-300">
        <cfg.Icon size={11} className={`${cfg.color} shrink-0`} />
        <span className="font-semibold text-zinc-100 truncate">{m.case_name || "Case"}</span>
        <span className="text-zinc-500">·</span>
        <span className="text-slate-400 text-[9px] font-mono uppercase tracking-widest whitespace-nowrap">remediation</span>
      </div>
      <div className="text-[10px] text-zinc-500 mt-0.5">
        {findingRef} {cfg.verb} by {authorName(item)}
      </div>
    </div>
  );
}

export function EventItem({ item, isLast, onReply, onResolve, authorDisplay, currentUserId }) {
  const isOwn       = !!(currentUserId && item.author_id && item.author_id === currentUserId);
  const isUnread    = item.unread && !isOwn;
  const activeDot   = EVENT_DOT[item.event_type] || EVENT_DOT.system;
  const dotColor    = isUnread ? activeDot : "bg-zinc-600";
  const tagStyle    = EVENT_TAG[item.event_type]  || EVENT_TAG.system;
  const isOptimistic = item.optimistic;
  const replies     = item.replies || [];

  const [showReplies,  setShowReplies]  = useState(false);
  const [replyOpen,    setReplyOpen]    = useState(false);

  const renderBody = (text) => {
    if (!text) return null;
    return text.split(/(@\w+)/g).map((part, i) =>
      part.startsWith("@")
        ? <span key={i} className="text-sky-400 font-semibold">{part}</span>
        : part
    );
  };

  const handleReplySubmit = async (text) => {
    await onReply(item.id, text);
    setReplyOpen(false);
    setShowReplies(true);
  };

  // Line extends if not last OR if there are replies/reply composer open
  const lineExtends = !isLast || replies.length > 0 || replyOpen;

  return (
    <div className={cx("flex gap-3", isOptimistic && "opacity-60")}>
      {/* Dot + connecting line */}
      <div className="flex flex-col items-center pt-1 shrink-0">
        <div className={cx("w-2 h-2 rounded-full shrink-0 ring-2 ring-zinc-900", dotColor)} />
        {lineExtends && <div className="w-px flex-1 min-h-[16px] mt-1 bg-zinc-800" />}
      </div>

      {/* Content */}
      <div className={cx("flex-1 min-w-0", (isLast && !replies.length && !replyOpen) ? "pb-0" : "pb-2")}>
        {/* Comment */}
        {item.event_type === "comment" && (
          <p className="text-[10px] text-zinc-300 leading-snug">
            <span className="font-semibold text-zinc-100">
              {authorName(item)}
            </span>{" "}
            {renderBody(item.body)}
          </p>
        )}

        {/* Link */}
        {item.event_type === "link" && (
          <div className="space-y-1">
            <p className="text-[10px] text-zinc-300 leading-snug">
              <span className="font-semibold text-zinc-100">
                {authorName(item)}
              </span>{" "}
              linked{" "}
              {item.metadata?.ref_url ? (
                <a
                  href={item.metadata.ref_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sky-400 underline hover:text-sky-300 transition-colors"
                >
                  {item.metadata?.ref_label || item.metadata?.ref_type || "reference"}
                </a>
              ) : (
                <span className="text-sky-400">
                  {item.metadata?.ref_label || item.metadata?.ref_type || "reference"}
                </span>
              )}
            </p>
            {/* Description — shown when body is more than the default label */}
            {item.body && item.body !== "Shared map view" && item.body !== "" && (
              <p className="text-[10px] text-zinc-400 leading-snug italic">
                "{item.body}"
              </p>
            )}
          </div>
        )}

        {/* Flag */}
        {item.event_type === "flag" && (
          <div className={cx(
            "rounded-xl ring-1 px-3 py-2",
            item.metadata?.status === "resolved"
              ? "bg-zinc-800/50 ring-zinc-700"
              : "bg-red-500/10 ring-red-500/20"
          )}>
            <div className="flex items-center justify-between gap-2 mb-1">
              <div className="flex items-center gap-2">
                <span className={cx(
                  "text-[10px] font-semibold font-mono uppercase tracking-widest",
                  item.metadata?.status === "resolved" ? "text-zinc-500" : "text-red-400"
                )}>
                  {item.metadata?.severity || "medium"}
                  {item.metadata?.category ? ` · ${item.metadata.category}` : " · flag"}
                </span>
                <span className={cx(
                  "text-[10px] px-1.5 py-0.5 rounded ring-1",
                  item.metadata?.status === "resolved"
                    ? "text-zinc-400 ring-zinc-700"
                    : "text-red-300 ring-red-800 bg-red-500/10"
                )}>
                  {item.metadata?.status || "open"}
                </span>
              </div>
              {/* Resolve / Reopen button */}
              {onResolve && (
                <button
                  onClick={() => onResolve(
                    item.id,
                    item.metadata?.status === "resolved" ? "open" : "resolved"
                  )}
                  className={cx(
                    "text-[10px] px-2 py-0.5 rounded-lg ring-1 ring-inset transition",
                    item.metadata?.status === "resolved"
                      ? "text-zinc-400 ring-zinc-700 hover:text-zinc-200 hover:ring-zinc-500"
                      : "text-emerald-300 ring-emerald-800 bg-emerald-500/10 hover:bg-emerald-500/20"
                  )}
                >
                  {item.metadata?.status === "resolved" ? "Reopen" : "Resolve"}
                </button>
              )}
            </div>
            <p className={cx("text-[10px]", item.metadata?.status === "resolved" ? "text-zinc-500 line-through" : "text-zinc-300")}>
              {item.body}
            </p>
            {item.metadata?.assignee_id && (
              <p className="text-[10px] text-zinc-500 mt-1">Assigned · {item.metadata.assignee_id}</p>
            )}
            {item.author_display && (
              <p className="text-[9px] text-zinc-500 mt-0.5">— {item.author_display}</p>
            )}
          </div>
        )}

        {/* D2.4 case events — dispatched from CASE_EVENT_RENDERERS, and a
            generic line for anything not in it. Render BEFORE the generic
            system|status fallback below so structured renderers win over the
            italic-body default. */}
        {item.event_type === "system" && item.metadata?.event_subtype && (
          renderCaseEvent(item)
        )}

        {/* System / status with NO subtype — the original italic-body default. */}
        {(item.event_type === "system" || item.event_type === "status")
          && !item.metadata?.event_subtype && (
          <p className="text-[10px] text-zinc-500 italic leading-snug">{item.body}</p>
        )}

        {/* Meta row + reply button */}
        <div className="flex items-center gap-2 mt-1.5">
          <span className="text-[9px] text-zinc-500">{timeLabel(item.created_at)}</span>
          <span className={cx("text-[10px] px-1.5 py-0.5 rounded ring-1", tagStyle)}>
            {item.event_type}
          </span>
          {isOptimistic && <span className="text-[9px] text-zinc-600">sending…</span>}
          {isUnread && (
            <span className="w-1.5 h-1.5 rounded-full bg-sky-400 inline-block" />
          )}
          {/* Reply button — only on top-level events */}
          {!item.parent_id && !isOptimistic && (
            <button
              onClick={() => setReplyOpen((o) => !o)}
              className="text-[10px] text-zinc-500 hover:text-zinc-300 transition-colors ml-1"
            >
              Reply
            </button>
          )}
        </div>

        {/* Replies toggle */}
        {replies.length > 0 && !replyOpen && (
          <button
            onClick={() => setShowReplies((s) => !s)}
            className="mt-2 flex items-center gap-1.5 text-[9px] text-zinc-500 hover:text-zinc-300 transition-colors"
          >
            <div className="w-3 h-px bg-zinc-700" />
            {showReplies
              ? "Hide replies"
              : `${replies.length} ${replies.length === 1 ? "reply" : "replies"}`
            }
          </button>
        )}

        {/* Replies list */}
        {showReplies && replies.length > 0 && (
          <div className="mt-2 space-y-3 pl-1">
            {replies.map((reply) => (
              <div key={reply.id} className={cx("flex gap-2", reply.optimistic && "opacity-60")}>
                <div className="w-1.5 h-1.5 rounded-full bg-zinc-600 shrink-0 mt-2" />
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] text-zinc-300 leading-snug">
                    <span className="font-semibold text-zinc-100">
                      {authorName(reply)}
                    </span>{" "}
                    {renderBody(reply.body)}
                  </p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-[10px] text-zinc-500">{timeLabel(reply.created_at)}</span>
                    {reply.optimistic && <span className="text-[10px] text-zinc-600">sending…</span>}
                    {reply.unread && reply.author_id !== currentUserId && <span className="w-1 h-1 rounded-full bg-sky-400 inline-block" />}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Inline reply composer */}
        {replyOpen && (
          <ReplyComposer
            onSubmit={handleReplySubmit}
            onCancel={() => setReplyOpen(false)}
            authorDisplay={authorDisplay}
          />
        )}
      </div>
    </div>
  );
}

// ─── Thread loading states ────────────────────────────────────────────────────

function ThreadEmpty() {
  return <p className="text-[10px] text-zinc-600 px-1">No events yet for this context.</p>;
}

function ThreadError({ error, onRetry }) {
  return (
    <div className="flex items-center gap-2 text-[10px] text-red-400">
      <AlertCircle className="w-4 h-4 shrink-0" />
      <span>{error}</span>
      <button onClick={onRetry} className="underline text-zinc-400 hover:text-zinc-200 transition">
        Retry
      </button>
    </div>
  );
}

// REM-422. A stream that is not delivering says so. Before this, a stream that
// gave up left the thread looking current while it silently stopped updating;
// the only trace was a console.warn.
function StreamNotice({ status, onReconnect }) {
  if (status === "reconnecting") {
    return (
      <div className="mx-5 mb-2 flex items-center gap-2 text-[10px] text-zinc-500">
        <Loader2 className="w-3 h-3 animate-spin shrink-0" />
        <span>Live updates paused — reconnecting…</span>
      </div>
    );
  }
  if (status === "stopped") {
    return (
      <div className="mx-5 mb-2 flex items-center gap-2 text-[10px] text-amber-400">
        <AlertCircle className="w-3 h-3 shrink-0" />
        <span>Live updates stopped. New activity will not appear until you reconnect or reload.</span>
        <button onClick={onReconnect} className="underline text-zinc-400 hover:text-zinc-200 transition">
          Reconnect
        </button>
      </div>
    );
  }
  return null;
}

function ThreadLoading() {
  return (
    <div className="flex items-center gap-2 text-[10px] text-zinc-500">
      <Loader2 className="w-4 h-4 animate-spin" />
      Loading…
    </div>
  );
}

// ─── Timeline grouping ───────────────────────────────────────────────────────

function getTimeGroup(ts) {
  const now      = new Date();
  const date     = new Date(ts);
  const todayStart     = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterdayStart = new Date(todayStart - 24 * 60 * 60 * 1000);
  const weekStart      = new Date(todayStart - 6 * 24 * 60 * 60 * 1000);
  const monthStart     = new Date(todayStart - 29 * 24 * 60 * 60 * 1000);

  if (date >= todayStart)     return "Today";
  if (date >= yesterdayStart) return "Yesterday";
  if (date >= weekStart)      return "This week";
  if (date >= monthStart)     return "This month";
  return "Older";
}

const GROUP_ORDER = ["Today", "Yesterday", "This week", "This month", "Older"];

function groupEvents(events) {
  const groups = {};
  for (const e of events) {
    const g = getTimeGroup(e.created_at);
    if (!groups[g]) groups[g] = [];
    groups[g].push(e);
  }
  // Return only groups that have events, in canonical order
  return GROUP_ORDER
    .filter((g) => groups[g]?.length)
    .map((g) => ({ label: g, events: groups[g] }));
}

function TimeGroupLabel({ label }) {
  return (
    <div className="flex items-center gap-2 py-1">
      <div className="h-px flex-1 bg-zinc-800" />
      <span className="text-[9px] font-semibold tracking-widest font-mono uppercase text-zinc-600 shrink-0">
        {label}
      </span>
      <div className="h-px flex-1 bg-zinc-800" />
    </div>
  );
}

// ─── Tab bodies ───────────────────────────────────────────────────────────────

function ThreadTab({ contextKey, scope, events, loading, error, onRetry, sendComment, sendReply, resolveFlag, authorDisplay, onPost, onOpenComposer, composerSeed, currentUserId }) {
  const sorted = [...events].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  return (
    <div className="space-y-4">
      <QuickActions
        scope={scope}
        contextKey={contextKey}
        onPost={onPost}
        onOpenComposer={onOpenComposer}
      />

      {scope === "case" ? (
        // D2.4 — Composer disabled in case scope for v0. Case events are
        // emitted automatically by stage transitions and case creation;
        // cross-org comment fan-out is a separate v1 design decision.
        <div className="px-3 py-3 text-[10px] text-zinc-500 italic text-center
                        rounded-lg ring-1 ring-inset ring-zinc-800 bg-zinc-900/40">
          Case events appear here automatically. Open the case to take action.
        </div>
      ) : (
        <Composer
          contextKey={contextKey}
          scope={scope}
          onSubmit={(text) => sendComment(contextKey, text, authorDisplay)}
          authorDisplay={authorDisplay}
          seed={composerSeed}
        />
      )}

      {loading && <ThreadLoading />}
      {!loading && error && <ThreadError error={error} onRetry={onRetry} />}
      {!loading && !error && sorted.length === 0 && <ThreadEmpty />}

      {!loading && sorted.length > 0 && (
        <div className="space-y-1">
          <p className="text-[10px] font-semibold tracking-widest font-mono uppercase text-zinc-500 mb-2 px-1">
            Timeline
          </p>
          {groupEvents(sorted).map(({ label, events: groupEvts }) => (
            <div key={label}>
              <TimeGroupLabel label={label} />
              <div className="mt-1">
                {groupEvts.map((e, i) => (
                  <EventItem
                    key={e.id}
                    item={e}
                    isLast={i === groupEvts.length - 1}
                    onReply={(parentId, text) => sendReply(contextKey, parentId, text, authorDisplay)}
                    onResolve={(eventId, status) => resolveFlag(contextKey, eventId, status)}
                    authorDisplay={authorDisplay}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ActivityTab({ events, loading, error, onRetry, authorDisplay, currentUserId }) {
  const all = [...events].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  return (
    <div className="space-y-1">
      {loading && <ThreadLoading />}
      {!loading && error && <ThreadError error={error} onRetry={onRetry} />}
      {!loading && !error && all.length === 0 && <p className="text-[10px] text-zinc-600">No activity yet.</p>}
      {!loading && !error && groupEvents(all).map(({ label, events: groupEvts }) => (
        <div key={label}>
          <TimeGroupLabel label={label} />
          <div className="mt-1">
            {groupEvts.map((e, i) => (
              <EventItem key={e.id} item={e} isLast={i === groupEvts.length - 1} onReply={() => {}} authorDisplay={authorDisplay} currentUserId={currentUserId} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function LinksTab({ events, loading, error, onRetry, authorDisplay, currentUserId }) {
  const links = events.filter((e) => e.event_type === "link");
  return (
    <div>
      {loading && <ThreadLoading />}
      {!loading && error && <ThreadError error={error} onRetry={onRetry} />}
      {!loading && !error && links.length === 0 && <p className="text-[10px] text-zinc-600">No linked reports or views yet.</p>}
      {links.map((e, i) => <EventItem key={e.id} item={e} isLast={i === links.length - 1} onReply={() => {}} authorDisplay={authorDisplay} currentUserId={currentUserId} />)}
    </div>
  );
}

// ─── Main export ──────────────────────────────────────────────────────────────

export default function CollaborationTimeline({
  contextKey,
  projectKey,
  orgKey,
  caseKey,
  caseName,
  caseAvailable,
  casePillVisible,
  caseCount,
  onOpenPicker,
  projectUnread,
  orgUnread,
  caseUnread,
  orgName,
}) {
  const closeRail    = useCollabRailStore((s) => s.closeRail);
  const railOpen     = useCollabRailStore((s) => s.open);
  const loadThread        = useCollaborationStore((s) => s.loadThread);
  const subscribeThread   = useCollaborationStore((s) => s.subscribeThread);
  const unsubscribeThread = useCollaborationStore((s) => s.unsubscribeThread);
  const streamStatus      = useCollaborationStore((s) => s.streamStatus[contextKey] ?? null);
  const markRead     = useCollaborationStore((s) => s.markRead);
  const sendComment  = useCollaborationStore((s) => s.sendComment);
  const sendReply    = useCollaborationStore((s) => s.sendReply);
  const resolveFlag  = useCollaborationStore((s) => s.resolveFlag);
  const postEvent    = useCollaborationStore((s) => s.postEvent);
  const contextScope = useCollaborationStore((s) => s.contextScope);
  const setScope     = useCollaborationStore((s) => s.setScope);
  const members      = useCollaborationStore((s) => s.members[contextKey] ?? EMPTY_ARRAY);

  const events  = useCollaborationStore((s) => s.threads[contextKey]?.events  ?? EMPTY_ARRAY);
  const loading = useCollaborationStore((s) => s.threads[contextKey]?.loading  ?? false);
  const error   = useCollaborationStore((s) => s.threads[contextKey]?.error    ?? null);

  const projectName   = useProjectStore((s) => s.activeProject?.name ?? null);
  const user          = useAuthStore((s) => s.user);
  const currentUserId = useAuthStore((s) => s.user?.id ?? null);
  const authorDisplay = user?.name || user?.email || "Me";

  const [tab, setTab] = useState("thread");

  useEffect(() => {
    loadThread(contextKey);
  }, [contextKey]);

  // SSE subscription — server pushes new events instantly via PG NOTIFY.
  // THERE IS NO POLL FALLBACK FOR THE THREAD, whatever this comment used to
  // say: only the notification bell polls (App.jsx, main.jsx). If the stream is
  // not delivering, the thread does not update; the store retries an outage
  // with a backoff and stops on a refusal, and StreamNotice below says which
  // (REM-422).
  useEffect(() => {
    subscribeThread(contextKey);
    return () => unsubscribeThread(contextKey);
  }, [contextKey]);

  // Mark read when the rail opens or contextKey changes — not on every events
  // update, which would cause an infinite loop (markRead → loadNotifications
  // → state update → events ref changes → markRead again)
  useEffect(() => {
    if (!railOpen) return;
    // Read current state directly rather than from the closure
    const thread = useCollaborationStore.getState().threads[contextKey];
    const hasUnread = (thread?.events || []).some((e) =>
      e.unread || (e.replies || []).some((r) => r.unread)
    );
    if (hasUnread) useCollaborationStore.getState().markRead(contextKey);
  }, [contextKey, railOpen]);

  useEffect(() => {
    setTab("thread");
  }, [contextScope]);

  const handleRetry = useCallback(() => {
    loadThread(contextKey, { force: true });
  }, [contextKey]);

  const handlePost = useCallback(async ({ type, body, metadata }) => {
    await postEvent(contextKey, { event_type: type, body, metadata });
  }, [contextKey, postEvent]);

  const [composerSeed, setComposerSeed] = useState(null);
  const handleOpenComposer = useCallback((seed) => {
    setComposerSeed({ text: seed, ts: Date.now() });
  }, []);

  const unreadCount = events.filter((e) => e.unread && e.author_id !== currentUserId).length;
  const { type, label } = parseContextLabel(contextKey, projectName, orgName, caseName);

  return (
    <div className="flex flex-col h-full bg-bg-softer">

      {/* Header — matches mockup: large icon, title, member avatars, event count */}
      <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-3 shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <div className={cx(
            "w-9 h-9 rounded-xl flex items-center justify-center shrink-0",
            contextScope === "org"  ? "bg-violet-600/20" :
            contextScope === "case" ? "bg-teal-600/20"   :
                                      "bg-blue-600/20"
          )}>
            {contextScope === "org"  && <Building2     className="w-4 h-4 text-violet-400" />}
            {contextScope === "case" && <GitBranch     className="w-4 h-4 text-teal-400"   />}
            {contextScope !== "org" && contextScope !== "case" && (
              <MessageSquare className="w-4 h-4 text-blue-400" />
            )}
          </div>
          <div className="min-w-0">
            <div className="text-[10px] font-semibold text-zinc-100">Collaboration</div>
            <div className="text-[10px] text-zinc-500 mt-0.5 truncate">{type} · {label}</div>
          </div>
        </div>

        <div className="flex items-center gap-3 shrink-0 pt-0.5">
          {/* Member avatars */}
          {members.length > 0 && (
            <div className="flex items-center">
              {members.map((m, i) => (
                <MemberAvatar key={m.id} initials={m.initials} colorIndex={i} />
              ))}
            </div>
          )}
          <span className="text-[10px] text-zinc-500">{events.length} events</span>
          <button
            onClick={closeRail}
            title="Close collaboration"
            className="w-6 h-6 flex items-center justify-center rounded-md text-zinc-500 hover:text-zinc-200 hover:bg-white/10 transition-colors"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Scope toggle */}
      <ScopeToggle
        scope={contextScope}
        onChange={setScope}
        projectUnread={projectUnread}
        orgUnread={orgUnread}
        caseUnread={caseUnread}
        caseAvailable={caseAvailable}
        casePillVisible={casePillVisible}
        caseCount={caseCount}
        onOpenPicker={onOpenPicker}
      />

      {/* Tabs */}
      <TabRow active={tab} onChange={setTab} unreadCount={unreadCount} />

      <StreamNotice
        status={streamStatus}
        onReconnect={() => { unsubscribeThread(contextKey); subscribeThread(contextKey); }}
      />

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-5 pb-5">
        {tab === "thread" && (
          <ThreadTab
            contextKey={contextKey}
            scope={contextScope}
            events={events}
            loading={loading}
            error={error}
            onRetry={handleRetry}
            sendComment={sendComment}
            sendReply={sendReply}
            resolveFlag={resolveFlag}
            authorDisplay={authorDisplay}
            onPost={handlePost}
            onOpenComposer={handleOpenComposer}
            composerSeed={composerSeed}
            currentUserId={currentUserId}
          />
        )}
        {tab === "activity" && (
          <ActivityTab events={events} loading={loading} error={error} onRetry={handleRetry} authorDisplay={authorDisplay} currentUserId={currentUserId} />
        )}
        {tab === "links" && (
          <LinksTab events={events} loading={loading} error={error} onRetry={handleRetry} authorDisplay={authorDisplay} currentUserId={currentUserId} />
        )}
      </div>

    </div>
  );
}
